import type { SiteAdapter } from "../adapters/base.js";
import { AdapterNotFoundError } from "./errors.js";

export class AdapterRegistry {
  readonly #adapters: readonly SiteAdapter[];

  constructor(adapters: readonly SiteAdapter[]) {
    if (adapters.length === 0) {
      throw new Error("Downloader requires at least one adapter");
    }
    const seen = new Set<string>();
    for (const a of adapters) {
      if (seen.has(a.id)) {
        throw new Error(`Duplicate adapter id: ${a.id}`);
      }
      seen.add(a.id);
    }
    this.#adapters = adapters;
  }

  /**
   * Return the first adapter whose `canHandle(url)` is true. **First-match
   * wins**: when multiple adapters claim the same URL, registration order
   * decides. Built-in adapters are registered before user-supplied ones, so a
   * custom adapter that overlaps a built-in host must be placed first.
   * Throws {@link AdapterNotFoundError} when none match.
   */
  resolve(url: string): SiteAdapter {
    for (const a of this.#adapters) {
      if (a.canHandle(url)) return a;
    }
    throw new AdapterNotFoundError(url);
  }

  tryResolve(url: string): SiteAdapter | null {
    for (const a of this.#adapters) {
      if (a.canHandle(url)) return a;
    }
    return null;
  }

  list(): readonly SiteAdapter[] {
    return this.#adapters;
  }
}
